import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { whenFalse, whenOnce } from "@arcgis/core/core/watchUtils";
import { MapContext } from "../../../../App/MapContextContainer";
import { useSetUpMap } from "../../../../App/useSetUpMap";
import TimeSlider from "@arcgis/core/widgets/TimeSlider";
import TimeExtent from "@arcgis/core/TimeExtent";
import TimeInterval from "@arcgis/core/TimeInterval";
import StatisticDefinition from "@arcgis/core/rest/support/StatisticDefinition";
import { HistoryContext } from '../../HistoryContextContainer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import { outlineOnlySymbol } from '../../../CustomAreas/components/Map/userAreaSymbol';
import WFSLayer from '@arcgis/core/layers/WFSLayer';
import Polygon from '@arcgis/core/geometry/Polygon';
import { getAreaAsCqlQueryString } from './getAreaAsCqlQueryString';
import * as promiseUtils from "@arcgis/core/core/promiseUtils";
import notificationsRenderer from '../../../../components/Maps/NotificationsRenderer';
import { AppSettingsContext } from "../../../../App/AppSettingsContextContainer";
import MapContainer from '../../../../components/Maps/MapContainer';
import useTestLayer from '../../../../App/useTestLayer';
import { newDeclarationsApiUrl, oldDeclarationsApiUrl } from '../../../../App/useNotificationsLayer';
import errors from '../../../../App/errors';
import { useTranslation } from 'react-i18next';

export default function HistoryMap() {
    const mapContext = useContext(MapContext);
    const appSettingsContext = useContext(AppSettingsContext);
    const elementRef = useRef(null);
    const { webmap, view } = useSetUpMap(elementRef, "6e34c9c7b08d41779b23bf1241649275");
    const historyContext = useContext(HistoryContext);
    const areaToInspectLayer = useMemo(() => new GraphicsLayer({ title: "Area to inspect", listMode: 'hide' }), []);
    const timeSliderInitialised = useRef(false);
    const timesliderWatcher = useRef<__esri.WatchHandle | null>(null);
    const { t, i18n } = useTranslation("history");

    const labelFormatFunction = useCallback((value: Date | Date[], type: "min" | "max" | "extent" | undefined, element: HTMLElement | undefined) => {
        let shortDate = new Intl.DateTimeFormat("fi", {
            dateStyle: "short"
        });

        const dateToHandle: Date = Array.isArray(value) ? value[0] : value;

        let dateString = shortDate.format(dateToHandle);

        if (type === 'extent') {
            dateString = t("aikajanaOtsikko", { date: dateString });
        }

        if (element) {
            element.innerText = dateString;
        }
    }, [t, i18n.language]);

    const timeSlider = useMemo(() => historyContext.areaToInspect && new TimeSlider({
        container: "timeSliderDiv",
        view: view,
        playRate: 2000,
        mode: "instant",
        timeExtent: {
            start: new Date(2000, 0, 1),
            end: new Date(2000, 0, 1)
        },
        labelFormatFunction,
        stops: {
            interval: new TimeInterval({
                value: 1,
                unit: "months"
            })
        },
        disabled: true
    }), [historyContext.areaToInspect, t, i18n.language]);

    const ows = useMemo(() => new WFSLayer({
        url: (process.env.REACT_APP_USE_OLD_DECLARATION_URL === "true" ? oldDeclarationsApiUrl : newDeclarationsApiUrl),
        title: t("mkiKerrosOtsikko"),
        copyright: "Suomen metsäkeskus",
        spatialReference: { wkid: 4326 },
        renderer: notificationsRenderer,
        customParameters: {
            cql_filter: "1 = 0"
        }
    }), []);

    useEffect(() => {
        if (ows) {
            ows.title = t("mkiKerrosOtsikko");
        }
    }, [t, i18n.language]);

    useTestLayer((process.env.REACT_APP_USE_OLD_DECLARATION_URL === "true" ? oldDeclarationsApiUrl : newDeclarationsApiUrl) + "?SERVICE=WFS&REQUEST=GetCapabilities&VERSION=2.0.0&format=json", errors.notificationLayerLoadError);

    let sumArea = new StatisticDefinition({
        onStatisticField: "AREA",
        outStatisticFieldName: "sumArea",
        statisticType: "sum"
    });

    let avgAge = new StatisticDefinition({
        onStatisticField: "MEANAGE",
        outStatisticFieldName: "avgAge",
        statisticType: "avg"
    });

    useEffect(() => {
        if (mapContext.mapAndViewReady) {
            webmap.add(areaToInspectLayer);
            webmap.add(ows);

            view.whenLayerView(ows).then((owsLayerView) => {
                whenFalse(owsLayerView, "updating", async () => {
                    const queryArea = ows.createQuery();

                    queryArea.groupByFieldsForStatistics = ["CUTTINGREALIZATIONPRACTICE"];
                    queryArea.outStatistics = [sumArea];

                    const response = await ows.queryFeatures(queryArea);

                    const queryAge = ows.createQuery();

                    queryAge.groupByFieldsForStatistics = ["CUTTINGREALIZATIONPRACTICE"];
                    queryAge.outStatistics = [avgAge];
                    queryAge.where = "NOT MEANAGE IS NULL AND MEANAGE > 0";

                    const responseAge = await ows.queryFeatures(queryAge);

                    const combined = response.features.map(f => {
                        const age = responseAge.features.filter(a => a.attributes.CUTTINGREALIZATIONPRACTICE === f.attributes.CUTTINGREALIZATIONPRACTICE);

                        if (age.length === 1) {
                            return {
                                ...f,
                                attributes: {
                                    ...(f.attributes),
                                    avgAge: age[0].attributes.avgAge
                                }
                            }
                        }

                        return f;
                    });

                    historyContext.setStatistics(combined);
                });
            });
        }
    }, [mapContext.mapAndViewReady, webmap]);

    useEffect(() => {
        if (timeSlider && timeSliderInitialised.current === false) {
            const start = new Date(2000, 0, 1);
            const now = new Date();

            timeSlider.fullTimeExtent = new TimeExtent({
                start: start,
                end: new Date(now.getFullYear(), now.getMonth(), 1)
            });

            timeSliderInitialised.current = true;

            view.ui.add(timeSlider, "top-left");
        }
    }, [timeSlider]);

    function getCuttingPracticeCql() {
        if (appSettingsContext.appliedFilters?.cuttingRealizationPractices && appSettingsContext.appliedFilters?.cuttingRealizationPractices.length > 0) {
            let filterString = " AND "

            filterString = filterString + `${"CUTTINGREALIZATIONPRACTICE"} IN (${appSettingsContext.appliedFilters.cuttingRealizationPractices})`;

            return filterString;
        }

        return "";
    }

    const updateBBOX = promiseUtils.debounce(() => {
        return new Promise((resolve) => {
            if (historyContext.areaToInspect && timeSlider) {
                const asPolygon = historyContext.areaToInspect as Polygon;

                let cqlPolygonString = getAreaAsCqlQueryString(asPolygon);
                let cqlCuttingPracticeString = getCuttingPracticeCql();

                ows.customParameters = {
                    propertyName: `(DECLARATIONARRIVALDATE,AREA,CUTTINGREALIZATIONPRACTICE,${historyContext.showNotificationsOnMap ? "GEOMETRY" : ""},MEANAGE)`,
                    cql_filter: `DECLARATIONARRIVALDATE BEFORE ${timeSlider.timeExtent.start.getFullYear()}-${(timeSlider.timeExtent.start.getMonth() + 1 + "").padStart(2, "0")}-01T00:00:00Z AND ` +
                        `DECLARATIONARRIVALDATE AFTER ${timeSlider.timeExtent.start.getFullYear() - 3}-${(timeSlider.timeExtent.start.getMonth() + 1 + "").padStart(2, "0")}-01T00:00:00Z AND ` +
                        cqlPolygonString +
                        cqlCuttingPracticeString
                };

                ows.refresh();

                whenOnce(ows, "refresh", resolve);
            }
        });
    });

    useEffect(() => {
        if (historyContext.areaToInspect && timeSlider) {
            if (timesliderWatcher.current !== null) {
                timesliderWatcher.current.remove();
            }

            timesliderWatcher.current = timeSlider.watch("timeExtent", async () => {
                if (timeSlider.timeExtent.start.getTime() !== new Date(2000, 0, 1).getTime()) {
                    updateBBOX().catch();
                }
            });

            // Run once to get first data
            updateBBOX().catch();

            timeSlider.disabled = false;

            areaToInspectLayer.removeAll();

            areaToInspectLayer.add(new Graphic({
                geometry: historyContext.areaToInspect, symbol: outlineOnlySymbol
            }));
        }
        else {
            areaToInspectLayer.removeAll();
        }
    }, [historyContext.areaToInspect, timeSlider, historyContext.showNotificationsOnMap, appSettingsContext.appliedFilters]);

    return <MapContainer elementRef={elementRef} view={view}></MapContainer>
}
